import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Pressable } from 'react-native'
import { useSelector } from 'react-redux'

import styled, { useTheme } from 'styled-components/native'

import { DocumentType as ModelDocumentType } from '@lyrahealth-inc/shared-app-logic/src/models/document/Document'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { InputField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { CheckIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CheckIcon'
import { FilterIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/FilterIcon'
import { NumberBadge } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/NumberBadge'
import { PaperclipIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/PaperclipIcon'
import { SearchIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SearchIcon'
import { SignatureIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SignatureIcon'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { InfoPopover } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/infoPopover/InfoPopover'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { UnstyledIconButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/unstyledIconButton/UnstyledIconButton'
import {
  DocumentFiltersPopover,
  DocumentFiltersState,
  DocumentType,
  FilterSectionConfig,
  INITIAL_DOCUMENT_FILTER_STATE,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/documentFilters/DocumentFilters'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { getAuthUserId } from '../../../../../data/auth/authSelectors'
import { Template, useGetDocumentTemplatesQuery } from '../../../../documents/data/templatesApi'

const TYPE_FILTER_MAP: Record<DocumentType, ModelDocumentType> = {
  [DocumentType.INTAKE_FORM]: ModelDocumentType.INTAKE_FORM,
  [DocumentType.ASSESSMENT]: ModelDocumentType.ASSESSMENT,
  [DocumentType.ACTIVITY]: ModelDocumentType.ACTIVITY,
  [DocumentType.OTHER]: ModelDocumentType.OTHER,
}

const FILTER_SECTIONS: FilterSectionConfig<DocumentFiltersState>[] = [
  {
    key: 'type',
    label: 'Type',
    options: Object.values(DocumentType).map((t) => ({ id: t, text: t })),
  },
]

const Container = styled.View({
  flex: 1,
  flexDirection: 'column',
})

const SearchRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  padding: theme.spacing['16px'],
  gap: theme.spacing['8px'],
  borderBottomWidth: '1px',
  borderBottomColor: theme.colors.borderDefault,
}))

const SearchIconWrapper = styled.View({
  width: 24,
  height: 24,
  justifyContent: 'center',
  alignItems: 'center',
})

const DocumentList = styled.ScrollView({
  flex: 1,
})

const DocumentListItem = styled(PressableOpacity)(({ theme }) => ({
  paddingVertical: theme.spacing['12px'],
  paddingHorizontal: theme.spacing['16px'],
  borderBottomWidth: '1px',
  borderBottomColor: theme.colors.borderDefault,
}))

const DocumentRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingVertical: 4,
})

const SignatureIconWrapper = styled.View({
  flexShrink: 0,
  marginLeft: 6,
  alignSelf: 'center',
})

const AttachedRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  alignSelf: 'flex-start',
  justifyContent: 'center',
  width: '100%',
  gap: theme.spacing['8px'],
  marginTop: theme.spacing['8px'],
  paddingVertical: theme.spacing['8px'],
  paddingHorizontal: theme.spacing['12px'],
  backgroundColor: theme.colors.backgroundSuccess,
  borderRadius: '8px',
}))

const FilterIconContainer = styled.View(({ theme }) => ({
  padding: theme.spacing['12px'],
}))

const NumberBadgeContainer = styled(NumberBadge)({
  position: 'absolute',
  top: 0,
  right: 0,
})

type MessengerDocumentLibrarySideBarProps = {
  onAttachDocument: (template: Template) => void
}

const MessengerDocumentLibrarySideBar: React.FC<MessengerDocumentLibrarySideBarProps> = ({ onAttachDocument }) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  const [searchText, setSearchText] = useState('')
  const [filterState, setFilterState] = useState<DocumentFiltersState>(INITIAL_DOCUMENT_FILTER_STATE)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [attachedTemplateId, setAttachedTemplateId] = useState<string | null>(null)
  const providerId = useSelector(getAuthUserId)

  const activeFilters = useMemo(() => (filterState.type.length > 0 ? 1 : 0), [filterState])

  useEffect(() => {
    if (!attachedTemplateId) return
    const timeout = setTimeout(() => setAttachedTemplateId(null), 2000)
    return () => clearTimeout(timeout)
  }, [attachedTemplateId])

  const { data: templatesData } = useGetDocumentTemplatesQuery(providerId ? { providerId } : undefined, {
    skip: !providerId,
  })

  const filteredTemplates = useMemo(() => {
    const templates = templatesData?.document_templates ?? []
    return templates.filter((template) => {
      if (searchText && !template.file_name.toLowerCase().includes(searchText.toLowerCase())) {
        return false
      }
      if (filterState.type.length > 0) {
        const modelTypes = filterState.type.map((t) => TYPE_FILTER_MAP[t])
        if (!modelTypes.includes(template.document_type as ModelDocumentType)) {
          return false
        }
      }
      return true
    })
  }, [templatesData, filterState, searchText])

  return (
    <Container testID={tID('MessengerDocumentLibrarySideBar')}>
      <SearchRow>
        <InputField
          testID={tID('MessengerDocumentLibrarySideBar-search')}
          placeholder={formatMessage({
            defaultMessage: 'Search file name',
            description: 'Placeholder for document library search input in messenger sidebar',
          })}
          value={searchText}
          onChange={(e) => setSearchText(e.nativeEvent.text)}
          baseInputStyle={{ flex: 1, marginBottom: 0 }}
          leftIcon={
            <SearchIconWrapper>
              <SearchIcon fillColor={colors.iconDefault} size={20} />
            </SearchIconWrapper>
          }
          inputExtras={
            searchText ? (
              <UnstyledIconButton
                testID={tID('MessengerDocumentLibrarySideBar-clear-search')}
                onPress={() => setSearchText('')}
                accessibilityLabel={formatMessage({
                  defaultMessage: 'Clear search',
                  description: 'Accessibility label for clear search button in document library',
                })}
              >
                <XIcon fillColor={colors.iconDefault} size={20} />
              </UnstyledIconButton>
            ) : undefined
          }
        />
        <InfoPopover
          placement='bottom left'
          popoverTriggerAction='press'
          shouldDisplayCloseButton={false}
          unstyledWrapper
          popoverStyle={{
            container: {
              padding: 0,
              paddingLeft: 16,
              paddingBottom: 8,
              boxShadow: 'none',
              backgroundColor: 'transparent',
            },
          }}
          clickToClose
          trigger={
            <Pressable
              testID={tID('DocumentFilters-filter-button')}
              accessibilityLabel={formatMessage({
                defaultMessage: 'Filter documents',
                description: 'Accessibility label for button to filter documents in document library',
              })}
              style={{ borderRadius: 100 }}
            >
              <FilterIconContainer>
                <FilterIcon size={24} />
              </FilterIconContainer>
              {activeFilters > 0 && <NumberBadgeContainer number={activeFilters} />}
            </Pressable>
          }
          content={
            <DocumentFiltersPopover
              filterState={filterState}
              onFilterStateChange={setFilterState}
              sections={FILTER_SECTIONS}
            />
          }
        />
      </SearchRow>
      <DocumentList testID={tID('MessengerDocumentLibrarySideBar-list')}>
        {filteredTemplates.length === 0 ? (
          <DocumentListItem>
            <BodyText
              text={formatMessage({
                defaultMessage: 'No documents available',
                description: 'Empty state message for document library in messenger sidebar',
              })}
              color={colors.textSecondary}
            />
          </DocumentListItem>
        ) : (
          filteredTemplates.map((template) => {
            const isSelected = template.document_template_id === selectedTemplateId
            const isAttached = template.document_template_id === attachedTemplateId
            const isDocuSign = !!template.signing_flow
            return (
              <DocumentListItem
                key={template.document_template_id}
                testID={tID(`MessengerDocumentLibrarySideBar-item-${template.document_template_id}`)}
                onPress={() => setSelectedTemplateId(isSelected ? null : template.document_template_id)}
                accessibilityRole='button'
                hoveredBackgroundColor={colors.backgroundSecondary}
                pressedBackgroundColor={colors.backgroundSecondary}
              >
                <DocumentRow>
                  <BodyText text={template.file_name} color={colors.textPrimary} />
                  {isDocuSign && (
                    <SignatureIconWrapper>
                      <Tooltip
                        content={formatMessage({
                          defaultMessage: 'Signature required',
                          description: 'Tooltip for signature required icon',
                        })}
                        hoverEnabled
                        placement='top'
                      >
                        <SignatureIcon width={16} height={16} fillColor={colors.iconDefault} />
                      </Tooltip>
                    </SignatureIconWrapper>
                  )}
                </DocumentRow>
                {isSelected && (
                  <SecondaryButton
                    testID={tID(`MessengerDocumentLibrarySideBar-attach-${template.document_template_id}`)}
                    text={formatMessage({
                      defaultMessage: 'Attach to message',
                      description: 'Button to attach the selected document to the chat message',
                    })}
                    leftIcon={<PaperclipIcon />}
                    onPress={() => {
                      onAttachDocument(template)
                      setSelectedTemplateId(null)
                      setAttachedTemplateId(template.document_template_id)
                    }}
                    style={{ marginTop: 8, width: '100%' }}
                  />
                )}
                {isAttached && (
                  <AttachedRow>
                    <CheckIcon
                      size={24}
                      fillColor={colors.textSuccessAlternate}
                      foregroundColor={colors.backgroundPrimary}
                    />
                    <BodyText
                      text={formatMessage({
                        defaultMessage: 'Attached',
                        description: 'Success message shown after a document is attached to a message',
                      })}
                      color={colors.textSuccessAlternate}
                    />
                  </AttachedRow>
                )}
              </DocumentListItem>
            )
          })
        )}
      </DocumentList>
    </Container>
  )
}

export default MessengerDocumentLibrarySideBar
